import { useCallback, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { AnalysisDimensionFilter } from '@models/analysis';
import { MultiDropdown, Option } from '@components/form/Dropdown.component';
import Dropdown from '@components/form/Dropdown.component';
import { useUniqueValuesInDimension } from '@models/reportBuilder';
import { Form } from 'react-bootstrap';

interface SingleDimensionFilterProps {
    dimensionFilter: AnalysisDimensionFilter;
    onChange: (newFilter: AnalysisDimensionFilter) => void;
    dimensionOptions?: Option[];
    onDelete?: () => void;
    displayType: 'table-row' | 'form';
    allowComparatorSelection?: boolean;
    comparatorOptions?: Option[];
}

const SingleDimensionFilter = (props: SingleDimensionFilterProps) => {
    const [query, setQuery] = useState<string>('');
    const [debouncedQuery] = useDebounce(query, 2000);

    const { data: dimensionValues, isLoading } = useUniqueValuesInDimension(props.dimensionFilter.dimension_id, debouncedQuery);
    
    const comparator = props.dimensionFilter.comparator || 'IN';
    const supportsMultipleValues = comparator === 'IN';
    const comparatorsWithoutValues = useMemo(
        () => ['EMPTY', 'NOT_EMPTY', 'VALID_DATE', 'INVALID_DATE', 'VALID_NUMBER', 'INVALID_NUMBER'],
        []
    );
    const requiresValue = !comparatorsWithoutValues.includes(comparator);

    const valueOptions = useMemo(() => {
        if (!dimensionValues) return [];
        return dimensionValues.map(v => ({
            value: v,

            // If the value is longer than 50 characters, truncate it and add an ellipses
            label: v.length > 50 ? `${v.slice(0, 50)}...` : v,
        }));
    }, [dimensionValues]);

    const valueForDropdown = useMemo(() => {
        if (!supportsMultipleValues) {
            return [];
        }
        if (!props.dimensionFilter.value) return [];
        if (typeof props.dimensionFilter.value === 'string') {
            return [props.dimensionFilter.value];
        }
        return props.dimensionFilter.value;
    }, [props.dimensionFilter.value, supportsMultipleValues]);

    const valueForInput = useMemo(() => {
        if (!requiresValue || supportsMultipleValues) {
            return '';
        }
        if (!props.dimensionFilter.value) {
            return '';
        }
        if (Array.isArray(props.dimensionFilter.value)) {
            return props.dimensionFilter.value.length ? String(props.dimensionFilter.value[0]) : '';
        }
        return String(props.dimensionFilter.value);
    }, [props.dimensionFilter.value, requiresValue, supportsMultipleValues]);

    const handleComparatorChange = useCallback((newComparator: string) => {
        const noValueComparators = new Set(comparatorsWithoutValues);
        const multiValueComparators = new Set(['IN']);

        let nextValue: string | string[] | undefined;
        if (multiValueComparators.has(newComparator)) {
            const currentValue = props.dimensionFilter.value;
            if (Array.isArray(currentValue)) {
                nextValue = currentValue;
            } else if (typeof currentValue === 'string' && currentValue.length) {
                nextValue = [currentValue];
            } else {
                nextValue = [];
            }
        } else if (noValueComparators.has(newComparator)) {
            nextValue = undefined;
        } else {
            const currentValue = props.dimensionFilter.value;
            if (Array.isArray(currentValue)) {
                nextValue = currentValue.length ? String(currentValue[0]) : '';
            } else if (typeof currentValue === 'string') {
                nextValue = currentValue;
            } else if (currentValue != null) {
                nextValue = String(currentValue);
            } else {
                nextValue = '';
            }
        }

        props.onChange({
            ...props.dimensionFilter,
            comparator: newComparator,
            value: nextValue,
        });
    }, [comparatorsWithoutValues, props.dimensionFilter, props.onChange]);

    if (props.displayType == 'table-row') {
        return <tr>
            <td>
                <Dropdown
                    options={props.dimensionOptions || []}
                    selected={props.dimensionFilter.dimension_id}
                    onChange={(newval) => {
                        props.onChange({
                            ...props.dimensionFilter,
                            dimension_id: newval,
                            comparator: 'IN',
                        });
                    }}
                />
            </td>
            {props.allowComparatorSelection && <td>
                <Dropdown
                    options={props.comparatorOptions || [{ label: 'is one of', value: 'IN' }]}
                    selected={comparator}
                    onChange={handleComparatorChange}
                />
            </td>}
            <td>
                <div className="d-flex center-vertically w-100">
                    {!requiresValue && <span className="badge bg-warning text-dark">
                        {comparator === 'EMPTY' ? 'Empty' : comparator === 'NOT_EMPTY' ? 'Not Empty' : comparator.replace('_', ' ')}
                    </span>}
                    {requiresValue && supportsMultipleValues && <>
                        <MultiDropdown
                            allowCreate
                            className="flex-1"
                            options={valueOptions}
                            selected={valueForDropdown}
                            onChange={(newval) => {
                                props.onChange({
                                    ...props.dimensionFilter,
                                    value: newval,
                                    comparator: 'IN',
                                });
                            }}
                            onInputChange={(newQuery) => setQuery(newQuery)}
                        />
                        {isLoading && <i className="mdi mdi-loading mdi-spin ms-2" />}
                        {!isLoading && <i className="mdi mdi-check ms-2" />}
                    </>}
                    {requiresValue && !supportsMultipleValues && (
                        <Form.Control
                            type="text"
                            value={valueForInput}
                            onChange={(event) => {
                                props.onChange({
                                    ...props.dimensionFilter,
                                    value: event.target.value,
                                });
                            }}
                        />
                    )}
                </div>
            </td>
            <td className="text-end">
                <button className="icon-button" onClick={props.onDelete}>
                    <i className="mdi mdi-delete"></i>
                </button>
            </td>
        </tr>
    }
    return <div>
        
        <div className="d-flex center-vertically w-100">
            <div className="flex-grow-1">
                {props.allowComparatorSelection && (
                    <>
                        <div className="mb-2">
                            <Dropdown
                                options={props.dimensionOptions || []}
                                selected={props.dimensionFilter.dimension_id}
                                onChange={(newval) => {
                                    props.onChange({
                                        ...props.dimensionFilter,
                                        dimension_id: newval,
                                        comparator: 'IN',
                                    });
                                }}
                            />
                        </div>
                        <div className="mb-2">
                            <Dropdown
                                options={props.comparatorOptions || [{ label: 'is one of', value: 'IN' }]}
                                selected={comparator}
                                onChange={handleComparatorChange}
                            />
                        </div>
                    </>
                )}
                <div className="d-flex center-vertically w-100">
                    {!requiresValue && (
                        <span className="badge bg-warning text-dark">
                            {comparator === 'EMPTY'
                                ? 'Empty'
                                : comparator === 'NOT_EMPTY'
                                ? 'Not Empty'
                                : comparator.replace('_', ' ')}
                        </span>
                    )}
                    {requiresValue && supportsMultipleValues && (
                        <>
                            <MultiDropdown
                                allowCreate
                                className="flex-1"
                                options={valueOptions}
                                selected={valueForDropdown}
                                onChange={(newval) => {
                                    props.onChange({
                                        ...props.dimensionFilter,
                                        value: newval,
                                        comparator: 'IN',
                                    });
                                }}
                                onInputChange={(newQuery) => setQuery(newQuery)}
                            />
                            {isLoading && <i className="mdi mdi-loading mdi-spin ms-2" />}
                            {!isLoading && <i className="mdi mdi-check ms-2" />}
                        </>
                    )}
                    {requiresValue && !supportsMultipleValues && (
                        <Form.Control
                            type="text"
                            value={valueForInput}
                            onChange={(event) => {
                                props.onChange({
                                    ...props.dimensionFilter,
                                    value: event.target.value,
                                });
                            }}
                        />
                    )}
                </div>
            </div>
            {props.allowComparatorSelection && (
                <div className="ms-2 font-18">
                    <button className="icon-button" onClick={props.onDelete} title="Remove filter" tabIndex={-1}>
                        <i className="mdi mdi-delete" />
                    </button>
                </div>
            )}
        </div>
    </div>
    
}

export default SingleDimensionFilter;